import { z } from 'zod';
import {
  isoDateSchema,
  longTextSchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import {
  EXPENSE_FREQUENCIES,
  EXPENSE_SCHEDULE_STATUSES,
  PAYMENT_METHODS,
  type ExpenseFrequency,
} from '../enums.js';

/**
 * Recurring expenses.
 *
 * ── A schedule is not an expense ───────────────────────────────────────────
 *
 * The rent agreement and the rent paid in March are different things, and
 * conflating them is how a business ends up reporting money it has not spent.
 * So a schedule is a TEMPLATE — amount, category, location, how often — and an
 * occurrence that is actually taken becomes an ordinary row in `expenses`,
 * indistinguishable from one somebody typed.
 *
 * Two consequences follow, and both are the point:
 *
 *  - **Nothing future is ever written.** Creating a monthly schedule inserts
 *    one row, not twelve. The dashboard and every report read `expenses` and
 *    therefore cannot see a scheduled amount, because there is nothing there
 *    to see. No report query changes.
 *  - **History is immutable by construction.** March's rent is its own row with
 *    its own amount. Raising the schedule in April cannot reach back into it,
 *    because the schedule is not where March's figure lives.
 *
 * An occurrence deliberately NOT taken is recorded too — see `skipSchema`. A
 * blank month and a month somebody decided to skip are different facts, and
 * only one of them needs explaining later.
 */

export const expenseScheduleInputSchema = z
  .object({
    amount: positiveMoneySchema,
    categoryId: uuidSchema.nullable().optional(),
    categoryName: shortTextSchema.optional(),
    description: longTextSchema.optional(),
    notes: longTextSchema.optional(),
    /** Money is spent somewhere. A schedule says where, like the expense does. */
    branchId: uuidSchema.nullable().optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),

    frequency: z.enum(EXPENSE_FREQUENCIES),
    /** Required for `custom`, ignored otherwise. */
    intervalDays: z.number().int().min(1).max(365).optional(),

    startsOn: isoDateSchema,
    /**
     * When the next one falls due.
     *
     * Settable at creation so a schedule can be entered for an arrangement
     * already running — rent that started in January, recorded in October,
     * should not fire nine occurrences to catch up.
     */
    nextDueOn: isoDateSchema.optional(),
    /** Null is "no end date", which is what most standing costs are. */
    endsOn: isoDateSchema.nullable().optional(),

    /**
     * Write the expense automatically when it falls due.
     *
     * Off by default. An expense that appears in the books without anybody
     * deciding is a figure nobody checked, and for most businesses confirming
     * each one is the point of having the list.
     */
    autoCreate: z.boolean().default(false),
    /** Days before the due date to start showing it as coming up. */
    reminderDays: z.number().int().min(0).max(90).nullable().optional(),
  })
  .refine((v) => v.frequency !== 'custom' || v.intervalDays !== undefined, {
    path: ['intervalDays'],
    message: 'A custom frequency needs an interval in days',
  })
  .refine((v) => !v.endsOn || v.endsOn >= v.startsOn, {
    path: ['endsOn'],
    message: 'The end date cannot be before the start',
  });
export type ExpenseScheduleInput = z.infer<typeof expenseScheduleInputSchema>;

/**
 * Editing a schedule changes the FUTURE and nothing else.
 *
 * `startsOn` is absent deliberately: it is where the recurrence is anchored,
 * and moving it would re-date occurrences that have already happened. Stop this
 * schedule and write a new one instead.
 */
export const expenseScheduleUpdateSchema = z.object({
  amount: positiveMoneySchema.optional(),
  categoryId: uuidSchema.nullable().optional(),
  description: longTextSchema.optional(),
  notes: longTextSchema.optional(),
  branchId: uuidSchema.nullable().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  frequency: z.enum(EXPENSE_FREQUENCIES).optional(),
  intervalDays: z.number().int().min(1).max(365).nullable().optional(),
  nextDueOn: isoDateSchema.optional(),
  endsOn: isoDateSchema.nullable().optional(),
  autoCreate: z.boolean().optional(),
  reminderDays: z.number().int().min(0).max(90).nullable().optional(),
});
export type ExpenseScheduleUpdate = z.infer<typeof expenseScheduleUpdateSchema>;

/** Take the occurrence that is due: write a real expense, advance the schedule. */
export const recordOccurrenceSchema = z.object({
  /**
   * Which occurrence the caller believes it is taking.
   *
   * Optional, and the screen always sends it: the date it displayed. If the
   * schedule has moved on since — somebody else recorded it, the nightly sweep
   * got there first, or the button was double-clicked — this no longer matches
   * and the second attempt is refused instead of quietly recording the NEXT
   * month. Without it, two rapid clicks pay January and February.
   */
  occurrenceOn: isoDateSchema.optional(),
  /** Defaults to the due date. Settable for "we actually paid it on the 3rd". */
  incurredOn: isoDateSchema.optional(),
  /** Defaults to the schedule's amount. A bill is rarely exactly the estimate. */
  amount: positiveMoneySchema.optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  notes: longTextSchema.optional(),
});
export type RecordOccurrenceInput = z.infer<typeof recordOccurrenceSchema>;

/**
 * Skip one occurrence.
 *
 * A reason is required. "The office was closed in August" and "nobody got round
 * to it" are different facts, and a gap in a recurring cost is exactly the
 * thing somebody asks about a year later.
 */
export const skipOccurrenceSchema = z.object({ reason: shortTextSchema });
export type SkipOccurrenceInput = z.infer<typeof skipOccurrenceSchema>;

export const expenseScheduleSchema = z.object({
  id: uuidSchema,
  amount: z.string(),
  currencyCode: z.string(),
  categoryId: uuidSchema.nullable(),
  categoryName: z.string().nullable(),
  description: z.string().nullable(),
  notes: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  paymentMethod: z.enum(PAYMENT_METHODS),

  frequency: z.enum(EXPENSE_FREQUENCIES),
  intervalDays: z.number().int().nullable(),
  startsOn: z.string(),
  nextDueOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  autoCreate: z.boolean(),
  reminderDays: z.number().int().nullable(),
  status: z.enum(EXPENSE_SCHEDULE_STATUSES),

  /** How many occurrences have actually been recorded. Never a projection. */
  occurrencesRecorded: z.number().int(),
  occurrencesSkipped: z.number().int(),
  /** What has really been spent under this schedule, to date. */
  totalRecorded: z.string(),
  lastRecordedOn: z.string().nullable(),
  /** True when `nextDueOn` is today or earlier and the schedule is running. */
  isDue: z.boolean(),
  /**
   * Archived. Hidden from the ordinary screens; still resolvable so an audit
   * entry or a historical occurrence can say what it came from.
   */
  deletedAt: z.string().nullable(),
  deletedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type ExpenseScheduleView = z.infer<typeof expenseScheduleSchema>;

export const expenseScheduleFiltersSchema = z.object({
  status: z.enum(EXPENSE_SCHEDULE_STATUSES).optional(),
  branchId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  /** Only the ones due on or before today. */
  dueOnly: z.coerce.boolean().optional(),
  /**
   * Include archived schedules.
   *
   * For an audit or history view that has to explain an old occurrence. The
   * ordinary Recurring card never sets it.
   */
  includeDeleted: z.coerce.boolean().optional(),
});
export type ExpenseScheduleFilters = z.infer<typeof expenseScheduleFiltersSchema>;

// ── The recurrence arithmetic ───────────────────────────────────────────────

/** Days in a month, 1-indexed month. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

const parse = (iso: string): [number, number, number] =>
  iso.split('-').map(Number) as [number, number, number];

const format = (y: number, m: number, d: number): string =>
  `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/**
 * Add whole months, CLAMPED to the end of the target month.
 *
 * `Date.UTC(2026, 1, 31)` is 3 March, which is the ordinary overflow and the
 * wrong answer for a bill: rent due on the 31st is due on 28 February, not in
 * March. Clamping is the behaviour every billing system eventually implements,
 * usually after somebody notices February missing.
 */
export function addMonthsClamped(iso: string, months: number): string {
  const [y, m, d] = parse(iso);
  const total = (y * 12 + (m - 1)) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return format(year, month, Math.min(d, daysInMonth(year, month)));
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = parse(iso);
  const at = new Date(Date.UTC(y, m - 1, d + days));
  return format(at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate());
}

/**
 * The occurrence after `current`.
 *
 * Monthly and yearly are anchored to `startsOn`, not stepped from the previous
 * occurrence. The difference shows on a schedule that begins on the 31st:
 * anchored gives 31 Jan, 28 Feb, 31 Mar, 30 Apr; stepped gives 31 Jan, 28 Feb,
 * 28 Mar, 28 Apr — the day of the month quietly walking backwards until the
 * rent is being paid a fortnight early.
 */
export function nextOccurrence(
  startsOn: string,
  current: string,
  frequency: ExpenseFrequency,
  intervalDays?: number | null,
): string {
  switch (frequency) {
    case 'daily':
      return addDays(current, 1);
    case 'weekly':
      return addDays(current, 7);
    case 'custom':
      return addDays(current, Math.max(1, intervalDays ?? 1));
    case 'monthly':
    case 'yearly': {
      const step = frequency === 'monthly' ? 1 : 12;
      const [sy, sm] = parse(startsOn);
      const [cy, cm] = parse(current);
      // How many whole steps `current` already is from the anchor, rounded
      // down, so an edited `nextDueOn` between two occurrences still lands on
      // the following scheduled date rather than drifting off the anchor.
      const elapsed = Math.floor(((cy * 12 + (cm - 1)) - (sy * 12 + (sm - 1))) / step);
      return addMonthsClamped(startsOn, (elapsed + 1) * step);
    }
  }
}

/** Has this schedule run out? `endsOn` null means it never does. */
export function hasEnded(nextDueOn: string | null, endsOn: string | null): boolean {
  if (!nextDueOn) return true;
  return endsOn !== null && nextDueOn > endsOn;
}
